import { AuditLogItem } from '../../store/services/auditLogService';
import { DAYS_IN_MONTH_APPROX, DAYS_IN_WEEK, MS_PER_DAY } from '../common/timeline/utils';

export type TimeGroup = 'Today' | 'Last 7 days' | 'Last 30 days' | 'Older';

export interface GroupedAuditLogItems {
    groupKey: TimeGroup;
    items: AuditLogItem[];
}

/**
 * Groups audit log items by time periods: Today, Last 7 days, Last 30 days, Older
 * @param items Array of audit log items to group
 * @returns Array of grouped items with group keys and items, filtered to exclude empty groups
 */
export function groupAuditLogsByTimePeriod(items: AuditLogItem[]): GroupedAuditLogItems[] {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const last7Days = new Date(today.getTime() - DAYS_IN_WEEK * MS_PER_DAY);
    const last30Days = new Date(today.getTime() - DAYS_IN_MONTH_APPROX * MS_PER_DAY);
    
    const groups: { [key in TimeGroup]: AuditLogItem[] } = {
        'Today': [],
        'Last 7 days': [],
        'Last 30 days': [],
        'Older': []
    };
    
    items.forEach((item: AuditLogItem) => {
        const itemDate = new Date(item.timeStamp);
        const itemDateOnly = new Date(itemDate.getFullYear(), itemDate.getMonth(), itemDate.getDate());
        
        if (itemDateOnly.getTime() >= today.getTime()) {
            groups['Today'].push(item);
        } else if (itemDateOnly.getTime() >= last7Days.getTime()) {
            groups['Last 7 days'].push(item);
        } else if (itemDateOnly.getTime() >= last30Days.getTime()) {
            groups['Last 30 days'].push(item);
        } else {
            groups['Older'].push(item);
        }
    });
    
    // Sort items within each group by timestamp (most recent first)
    Object.keys(groups).forEach(groupKey => {
        groups[groupKey as TimeGroup].sort((a, b) => new Date(b.timeStamp).getTime() - new Date(a.timeStamp).getTime());
    });
    
    // Filter out empty groups and maintain order
    const groupOrder: TimeGroup[] = ['Today', 'Last 7 days', 'Last 30 days', 'Older'];
    return groupOrder
        .filter(groupKey => groups[groupKey].length > 0)
        .map(groupKey => ({
            groupKey,
            items: groups[groupKey]
        }));
}

/**
 * Gets the first (most recent) group key that has items
 * @param groupedItems Array of grouped items
 * @returns The key of the first group, or null if no groups exist
 */
export function getFirstGroupKey(groupedItems: GroupedAuditLogItems[]): TimeGroup | null {
    return groupedItems.length > 0 ? groupedItems[0].groupKey : null;
}
